import React, { useCallback, useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { toast } from "react-toastify";
import api from "../../api";
import LoaderBlock from "../loader/LoaderBlock";
import Pagination from "../shared/Pagination";
import {
    ABC_META,
    buildParams,
    CHART_COLORS,
    fmtNumber,
    METHOD_LABELS,
    TREND_META,
} from "./analyticsUtils";

const TrendBadge = ({ trend }) => {
    const meta = TREND_META[trend] || TREND_META.flat;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.cls}`}>
            <i className={`fa-solid ${meta.icon}`} />
            {meta.label}
        </span>
    );
};

const ForecastPanel = ({ paramsBase }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [upc, setUpc] = useState("");
    const [search, setSearch] = useState("");

    const fetchForecast = useCallback(async () => {
        setLoading(true);
        try {
            const params = buildParams(paramsBase, { page, limit: 10, upc: search });
            const { data: res } = await api.get(`/api/v1/analytics/forecast?${params.toString()}`);
            if (res.success) setData(res);
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to load forecast");
        } finally {
            setLoading(false);
        }
    }, [paramsBase, page, search]);

    useEffect(() => {
        const timer = setTimeout(fetchForecast, 250);
        return () => clearTimeout(timer);
    }, [fetchForecast]);

    const agg = data?.aggregate;
    const categories = agg ? [...agg.labels, "Forecast"] : [];
    const historySeries = agg ? [...agg.history, null] : [];
    const forecastSeries = agg
        ? [
              ...Array(Math.max(agg.history.length - 1, 0)).fill(null),
              agg.history[agg.history.length - 1] ?? null,
              agg.forecast_next,
          ]
        : [];

    const chartOptions = {
        chart: { toolbar: { show: false }, fontFamily: "inherit", zoom: { enabled: false } },
        colors: [CHART_COLORS.blue, CHART_COLORS.purple],
        stroke: { curve: "smooth", width: [3, 3], dashArray: [0, 6] },
        markers: { size: [0, 5] },
        dataLabels: { enabled: false },
        grid: { borderColor: "#eef2f7", strokeDashArray: 4 },
        xaxis: {
            categories,
            labels: { style: { colors: "#94a3b8", fontSize: "11px" } },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: "#94a3b8", fontSize: "11px" } } },
        legend: { position: "top", horizontalAlign: "right" },
        tooltip: { theme: "light" },
    };

    const chartSeries = [
        { name: "Actual demand", data: historySeries },
        { name: "Forecast", data: forecastSeries },
    ];

    const rows = data?.rows || [];

    return (
        <div className="bg-white rounded-2xl shadow-1 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div>
                    <h3 className="font-bold text-slate-800 text-[15px]">
                        <i className="fa-solid fa-wand-magic-sparkles text-purple-500 mr-2" />
                        Demand Forecast
                    </h3>
                    <p className="text-[12px] text-slate-400">
                        Auto-selected statistical model per SKU (moving average / exponential smoothing / Holt trend).
                    </p>
                </div>
                {agg && (
                    <div className="text-right">
                        <p className="text-[12px] text-slate-400">Next-period total demand</p>
                        <p className="text-[20px] font-bold text-purple-600">
                            {fmtNumber(agg.forecast_next)} units
                        </p>
                    </div>
                )}
            </div>

            {agg && agg.history.some((v) => v > 0) ? (
                <Chart options={chartOptions} series={chartSeries} type="line" height={260} />
            ) : (
                <div className="h-[200px] flex-center text-slate-400 text-[13px]">
                    Not enough sales history to project demand yet.
                </div>
            )}

            <div className="flex items-center justify-between gap-2 mt-4 mb-2">
                <h4 className="font-semibold text-slate-700 text-[13px]">Per-SKU forecast</h4>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        setPage(1);
                        setSearch(upc.trim());
                    }}
                    className="flex items-center gap-2"
                >
                    <input
                        value={upc}
                        onChange={(e) => setUpc(e.target.value)}
                        placeholder="Search UPC"
                        className="border border-slate-200 rounded-lg px-3 py-1.5 text-[13px] outline-[var(--color-2)]"
                    />
                    <button
                        type="submit"
                        className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-[13px] font-semibold hover:bg-slate-200"
                    >
                        <i className="fa-solid fa-magnifying-glass" />
                    </button>
                </form>
            </div>

            {loading ? (
                <LoaderBlock />
            ) : rows.length === 0 ? (
                <p className="text-center text-slate-400 text-[13px] py-8">No SKUs to forecast.</p>
            ) : (
                <div className="overflow-x-auto my-scroll">
                    <table className="w-full text-[13px]">
                        <thead className="bg-slate-50 text-slate-500">
                            <tr>
                                <th className="th-text px-3 py-2 font-semibold">Product</th>
                                <th className="th-center px-3 py-2 font-semibold">Trend</th>
                                <th className="th-center px-3 py-2 font-semibold">Model</th>
                                <th className="th-num px-3 py-2 font-semibold">Avg/mo</th>
                                <th className="th-num px-3 py-2 font-semibold">Forecast</th>
                                <th className="th-num px-3 py-2 font-semibold">Range</th>
                                <th className="th-num px-3 py-2 font-semibold">On Hand</th>
                                <th className="th-center px-3 py-2 font-semibold">ABC</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.upc} className="border-b border-slate-50 hover:bg-slate-50">
                                    <td className="td-text px-3 py-2">
                                        <p className="font-semibold text-slate-700 truncate max-w-[220px]">
                                            {r.description}
                                        </p>
                                        <p className="text-[11px] text-slate-400">{r.upc} · {r.department}</p>
                                    </td>
                                    <td className="td-center px-3 py-2"><TrendBadge trend={r.trend} /></td>
                                    <td className="td-center px-3 py-2 text-slate-500 text-[12px]">
                                        {METHOD_LABELS[r.method] || r.method}
                                        {!r.has_enough_data && (
                                            <span title="Limited history" className="ml-1 text-amber-500">
                                                <i className="fa-solid fa-circle-info" />
                                            </span>
                                        )}
                                    </td>
                                    <td className="td-num px-3 py-2 text-slate-600">{r.avg_monthly_demand}</td>
                                    <td className="td-num px-3 py-2 font-bold text-purple-600">{r.forecast_next}</td>
                                    <td className="td-num px-3 py-2 text-slate-400 text-[12px]">
                                        {r.forecast_lower}–{r.forecast_upper}
                                    </td>
                                    <td className="td-num px-3 py-2 text-slate-600">{fmtNumber(r.on_hand)}</td>
                                    <td className="td-center px-3 py-2">
                                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${ABC_META[r.abc_class]}`}>
                                            {r.abc_class}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Pagination page={page} totalPages={data?.totalPages || 1} setPage={setPage} />
        </div>
    );
};

export default ForecastPanel;
